import { Link } from '@tanstack/react-router'
import { CircleAlert, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { NIGHT } from '~/art'
import { cx, Stage } from '~/ui'
import { useDocumentTitle } from './form'

const YEAR = new Date().getFullYear()

/** The SquadCore mark: a pitch's halfway line and centre circle. Follows the theme (ink on bg). */
export function BrandMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true" className={cx('size-7 shrink-0', className)}>
      <rect width="32" height="32" rx="8" className="fill-ink" />
      <circle cx="16" cy="16" r="7" fill="none" strokeWidth="2.5" className="stroke-ink-inverse" />
      <path d="M16 4v24" strokeWidth="2.5" className="stroke-ink-inverse" />
    </svg>
  )
}

export function Brand() {
  return (
    <Link to="/start" className="inline-flex items-center gap-2.5 rounded-control text-body font-semibold text-ink">
      <BrandMark />
      {/* Below 360 px the header needs the room for its two buttons; the mark alone carries the brand. */}
      <span className="max-[359px]:sr-only">SquadCore</span>
    </Link>
  )
}

/** Thin top bar + footer shared by every public page. */
export function PublicFrame({ aside, children }: { aside?: ReactNode; children: ReactNode }) {
  return (
    // Clips what comes out too wide instead of letting the page slide sideways (see AppShell).
    <div data-overflow-net className="flex min-h-dvh flex-col overflow-x-clip bg-bg">
      <header className="page-gutter mx-auto flex h-16 w-full max-w-[1200px] items-center justify-between gap-4 md:h-20">
        <Brand />
        {aside && <nav aria-label="Konto" className="flex items-center gap-1">{aside}</nav>}
      </header>
      <main id="main" className="flex flex-1 flex-col">
        {children}
      </main>
      <footer className="page-gutter mx-auto flex w-full max-w-[1200px] flex-wrap items-center justify-between gap-x-6 gap-y-2 py-8 text-small text-ink-3">
        <span>© {YEAR} SquadCore</span>
        <span className="flex gap-6">
          <Link to="/impressum" className="hover:text-ink">
            Impressum
          </Link>
          <Link to="/datenschutz" className="hover:text-ink">
            Datenschutz
          </Link>
        </span>
      </footer>
    </div>
  )
}

type AuthLayoutProps = {
  title: string
  /** One sentence under the title. */
  description?: ReactNode
  /** Small line above the title ("Einladung", "Schritt 2 von 4"). */
  eyebrow?: ReactNode
  children: ReactNode
  /** Links under the form ("Noch kein Konto? …"). */
  footer?: ReactNode
  aside?: ReactNode
}

/** Login, registration, password and invitation pages: one calm column, and on wide screens the ball on the
 *  touchline beside it (the landing's closing picture), so leaving the landing never feels like a jump. */
export function AuthLayout({ title, description, eyebrow, children, footer, aside }: AuthLayoutProps) {
  useDocumentTitle(title)
  return (
    <PublicFrame aside={aside}>
      <div className="page-gutter mx-auto grid w-full max-w-[1200px] flex-1 items-start gap-16 pt-8 pb-16 md:pt-[10vh] lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:pt-10">
        <div className="mx-auto w-full max-w-[400px] lg:mt-[8vh] lg:mr-auto lg:ml-0">
          {eyebrow && <p className="mb-3 text-small font-medium text-ink-3">{eyebrow}</p>}
          <h1 className="text-title text-ink text-balance md:text-page">{title}</h1>
          {description && <p className="mt-2 text-body text-ink-2 text-pretty">{description}</p>}
          <div className="mt-8">{children}</div>
          {footer && <div className="mt-8 border-t border-line pt-6 text-small text-ink-2">{footer}</div>}
        </div>
        <Stage
          art={NIGHT}
          focus="object-[63%_50%]"
          scrim="bottom"
          sizes="(min-width: 1296px) 620px, 50vw"
          className="sticky top-6 hidden min-h-[min(640px,calc(100dvh-160px))] flex-col justify-end lg:flex"
        >
          <div aria-hidden="true" className="p-10">
            <p className="text-[2rem] leading-[1.08] font-semibold tracking-[-0.03em] text-white">
              Weniger Zettel.
              <br />
              <span className="text-[#d6f35f]">Mehr Fußball.</span>
            </p>
          </div>
        </Stage>
      </div>
    </PublicFrame>
  )
}

/** Error that belongs to the whole form, right above its button. */
export function FormAlert({ children }: { children: ReactNode }) {
  if (!children) return null
  return (
    <p role="alert" className="flex items-start gap-2 rounded-control bg-negative-soft px-3 py-2.5 text-small text-negative">
      <CircleAlert aria-hidden="true" className="mt-px size-4 shrink-0" />
      <span>{children}</span>
    </p>
  )
}

/** Text link in auth copy. */
export function InlineLink({ to, search, children }: { to: string; search?: Record<string, string>; children: ReactNode }) {
  return (
    <Link to={to} search={search} className="font-medium text-ink underline decoration-line-strong underline-offset-4 hover:decoration-ink">
      {children}
    </Link>
  )
}

/** Outcome of a public flow (link sent, link expired, password saved): left-aligned like the forms. */
export function AuthNotice({ icon: Icon, title, text, action }: { icon: LucideIcon; title: ReactNode; text?: ReactNode; action?: ReactNode }) {
  return (
    <div>
      <div className="flex items-start gap-4 border-y border-line py-5">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-bg-subtle text-ink-2 ring-1 ring-line">
          <Icon aria-hidden="true" className="size-5" strokeWidth={1.75} />
        </span>
        <div className="flex min-w-0 flex-col gap-1">
          <p className="text-body font-medium text-ink text-pretty">{title}</p>
          {text && <p className="text-small text-ink-2 text-pretty">{text}</p>}
        </div>
      </div>
      {action && <div className="mt-8 flex flex-wrap gap-2">{action}</div>}
    </div>
  )
}
